import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCopy, FaRobot, FaUser } from 'react-icons/fa6';
import { NS } from '../U3204';
import { useU3204Context } from '../U3204Context';
import type { U3204DisplayMessage } from '../U3204Service';
import { u3204FormatFileSize } from '../utils/u3204AttachmentUtils';
import { u3204HasExportGenerationTool } from '../utils/u3204ExportMessageDisplay';
import { u3204HasMessageSources } from '../utils/u3204HasMessageSources';
import { u3204ResolveExportFile } from '../utils/u3204ResolveExportFile';
import U3204ExportDownloadCard from './common/U3204ExportDownloadCard';
import U3204MarkdownContent from './common/U3204MarkdownContent';
import U3204MessageSources from './common/U3204MessageSources';
import U3204ThinkingPanel from './common/U3204ThinkingPanel';
import U3204ToolActivityPanel from './common/U3204ToolActivityPanel';
import U3204MessageTokenBadge from './U3204MessageTokenBadge';

interface U3204MessageBubbleProps {
  message: U3204DisplayMessage;
  tokenCount?: number;
  tokenVariant?: 'input' | 'output';
}

function u3204MissionAvatarLabel(code?: string | null, title?: string | null): string {
  const source = (code ?? title ?? 'AI').trim();
  return source.slice(0, 2).toUpperCase();
}

const U3204MessageBubble: React.FC<U3204MessageBubbleProps> = ({ message, tokenCount, tokenVariant }) => {
  const { t } = useTranslation(NS);
  const { initLU, activeConversationId, activeMission } = useU3204Context();
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const isPending = message.role === 'assistant' && message.generationStatus === 'pending';
  const isFailed = message.isError || message.generationStatus === 'failed';
  const thinkingContent = message.thinkingContent?.trim() ?? '';
  const toolActivity = message.toolActivity ?? [];
  const hasToolActivity = !isUser && !isSystem && toolActivity.length > 0;
  const hasRunningTools = toolActivity.some((item) => item.status === 'running');
  const exportFile = useMemo(() => u3204ResolveExportFile(message), [message]);
  const isExportFlow =
    !isUser && !isSystem && (Boolean(exportFile) || u3204HasExportGenerationTool(message));
  /**
   * Pendant la boucle tools export, masquer le panneau réflexion (souvent ouvert + max-h + scroll interne) —
   * évite l’apparition/disparition de scrollbars avant la carte PDF/Word/Excel.
   */
  const showThinkingPanel =
    !isUser &&
    !isSystem &&
    initLU.showThinking &&
    thinkingContent.length > 0 &&
    !isExportFlow;
  const thinkingInProgress = showThinkingPanel && isPending && !message.content;
  const displayAttachments = useMemo(
    () =>
      (message.attachments ?? []).filter(
        (att) =>
          att.attachmentType !== 'excel' &&
          att.attachmentType !== 'word' &&
          att.attachmentType !== 'pdf_export',
      ),
    [message.attachments],
  );
  /** Masque le narratif LLM (ex. « Le document Word… ») et le texte « export prêt » : tools + carte suffisent. */
  const hideAssistantTextForExport = isExportFlow;
  const showBubbleContent = Boolean(message.content) && !hideAssistantTextForExport;
  const showPendingPlaceholder =
    isPending && !thinkingContent && !hasRunningTools && !exportFile && !showBubbleContent;
  const showArticle =
    isUser ||
    showBubbleContent ||
    displayAttachments.length > 0 ||
    Boolean(exportFile) ||
    showPendingPlaceholder ||
    isFailed;
  const showTokenUnderBubble = Boolean(tokenVariant);
  const showSources =
    !isUser && !isSystem && !isPending && u3204HasMessageSources(message.sources);

  const ariaLabel = useMemo(() => {
    if (isUser) {
      return t('messageRole.user', { ns: NS });
    }
    if (isSystem) {
      return t('messageRole.system', { ns: NS });
    }
    return t('messageRole.assistant', { ns: NS });
  }, [isSystem, isUser, t]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message.content);
    } catch {
      // ignore clipboard errors
    }
  }, [message.content]);

  if (isSystem) {
    return (
      <div className="flex w-full justify-center px-4">
        <p
          className="max-w-2xl rounded-lg border border-border/60 bg-muted/50 px-4 py-2 text-center text-xs text-muted-foreground"
          role="status"
          aria-label={ariaLabel}
        >
          {message.content}
        </p>
      </div>
    );
  }

  const bubbleClasses = isUser
    ? 'rounded-2xl rounded-br-md bg-primary-600 text-white shadow-medium'
    : isFailed
      ? 'w-full rounded-2xl border border-red-300 bg-red-50 text-neutral-900 shadow-soft'
      : 'w-full rounded-2xl rounded-bl-md border border-neutral-200 bg-surface text-neutral-900 shadow-soft';

  const rowMaxWidth = isUser ? 'max-w-[85%]' : 'max-w-[75%]';

  return (
    <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`flex ${rowMaxWidth} gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
        <div
          className={`mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
            isUser
              ? 'bg-primary-700 text-white ring-2 ring-primary-200'
              : 'border border-neutral-300 bg-neutral-100 text-neutral-700'
          }`}
          aria-hidden="true"
        >
          {isUser ? (
            <FaUser className="h-3.5 w-3.5" />
          ) : activeMission?.code || activeMission?.title ? (
            u3204MissionAvatarLabel(activeMission.code, activeMission.title)
          ) : (
            <FaRobot className="h-3.5 w-3.5" />
          )}
        </div>

        <div className={`flex min-w-0 flex-1 flex-col ${isUser ? 'items-end' : 'w-full items-stretch'}`}>
          {showThinkingPanel ? (
            <U3204ThinkingPanel
              conversationId={activeConversationId}
              thinkingContent={thinkingContent}
              isInProgress={thinkingInProgress}
            />
          ) : null}

          {hasToolActivity ? <U3204ToolActivityPanel toolActivity={toolActivity} /> : null}

          {showArticle ? (
            <article
              aria-label={ariaLabel}
              className={`group relative px-4 py-3 ${bubbleClasses} ${
                !isUser && showBubbleContent && !isPending ? 'pb-9' : ''
              }`}
            >
              {isUser ? (
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
              ) : showBubbleContent ? (
                <U3204MarkdownContent content={message.content} isStreaming={isPending} />
              ) : showPendingPlaceholder ? (
                <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                  <span className="animate-pulse">{t('generating', { ns: NS })}</span>
                  <span className="inline-block h-4 w-0.5 animate-pulse bg-current" />
                </span>
              ) : null}

              {isPending && showBubbleContent ? (
                <span className="ml-1 inline-block h-4 w-0.5 animate-pulse bg-current align-middle" />
              ) : null}

              {displayAttachments.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {displayAttachments.map((att) => (
                    <span
                      key={att.id}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                        isUser ? 'bg-white/20 text-white' : 'bg-neutral-100 text-neutral-700'
                      }`}
                    >
                      <span>📎</span>
                      <span>{att.fileName}</span>
                      <span className="opacity-70">({u3204FormatFileSize(att.fileSize)})</span>
                    </span>
                  ))}
                </div>
              ) : null}

              {exportFile ? (
                <div className={showBubbleContent || displayAttachments.length > 0 ? 'mt-3' : undefined}>
                  <U3204ExportDownloadCard exportFile={exportFile} />
                </div>
              ) : null}

              {!isUser && showBubbleContent && !isPending ? (
                <div className="absolute bottom-2 right-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white/95 px-2 py-1 text-xs text-neutral-600 shadow-xs hover:text-neutral-900 focus:outline-hidden focus:ring-2 focus:ring-primary-500/40"
                    title={t('copyMessage', { ns: NS })}
                    aria-label={t('copyMessage', { ns: NS })}
                  >
                    <FaCopy className="h-3 w-3" />
                    {t('copyMessage', { ns: NS })}
                  </button>
                </div>
              ) : null}
            </article>
          ) : null}

          {showSources ? <U3204MessageSources sources={message.sources} /> : null}

          {showTokenUnderBubble && tokenVariant ? (
            <U3204MessageTokenBadge count={tokenCount} variant={tokenVariant} />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default U3204MessageBubble;
